/**
 * Records every `user-error.shown` domain event as a FHIR `AuditEvent` in
 * Medplum, attributed to the signed-in practitioner, so disruptive user errors
 * can be queried and counted in one place.
 *
 * PHI: the message and route are scrubbed with the same functions as Datadog
 * telemetry, and no Patient reference is recorded.
 *
 * @agentic connections
 */

import { createReference, normalizeErrorString } from "@medplum/core";
import type { MedplumClient, ProfileResource } from "@medplum/core";
import type { AuditEvent } from "@medplum/fhirtypes";
import {
  DomainEventNames,
  type EventEmitter,
  type UserErrorShownEvent,
} from "@benchmark/core/events";
import {
  sanitizeTelemetryMessage,
  sanitizeTelemetryUrl,
} from "@/app/boot/telemetry";

/** CodeSystem for `AuditEvent.subtype` — the code is the event's `source`. @agentic connections */
export const USER_ERROR_SOURCE_SYSTEM =
  "https://benchmarkhealth.com/fhir/CodeSystem/user-error-source";

/** Identical errors inside this window are recorded once. @agentic connections */
export const USER_ERROR_DEDUPE_WINDOW_MS = 60_000;

/**
 * Build the AuditEvent for one user error.
 * @agentic connections
 */
export const buildUserErrorAuditEvent = (
  event: UserErrorShownEvent,
  profile: ProfileResource,
  location: { host: string; pathname: string },
): AuditEvent => ({
  resourceType: "AuditEvent",
  type: {
    system: "http://dicom.nema.org/resources/ontology/DCM",
    code: "110100",
    display: "Application Activity",
  },
  subtype: [{ system: USER_ERROR_SOURCE_SYSTEM, code: event.source }],
  action: "E",
  recorded: new Date().toISOString(),
  outcome: event.severity === "serious" ? "8" : "4",
  outcomeDesc: sanitizeTelemetryMessage(normalizeErrorString(event.error)),
  agent: [{ who: createReference(profile), requestor: true }],
  source: {
    site: location.host,
    observer: { display: "connections" },
    type: [
      {
        system: "http://terminology.hl7.org/CodeSystem/security-source-type",
        code: "1",
        display: "User Device",
      },
    ],
  },
  entity: [
    {
      what: { display: sanitizeTelemetryUrl(location.pathname) },
      ...(event.title ? { name: sanitizeTelemetryMessage(event.title) } : {}),
      detail: [
        { type: "version", valueString: __APP_VERSION__ },
        ...Object.entries(event.detail ?? {}).map(([type, value]) => ({
          type: sanitizeTelemetryMessage(type),
          valueString: sanitizeTelemetryMessage(String(value)),
        })),
      ],
    },
  ],
});

/**
 * Subscribe the AuditEvent writer to the event bus. Returns the unsubscribe.
 * Write failures are only logged: they never toast or re-publish, so a broken
 * write cannot loop.
 * @agentic connections
 */
export const connectUserErrorAuditEvents = (
  emitter: EventEmitter,
  medplum: MedplumClient,
): (() => void) => {
  // ponytail: per-tab in-memory dedupe; keys are never evicted and other tabs
  // still record. Upgrade to server-side dedupe if volume becomes a problem.
  const lastRecordedAt = new Map<string, number>();
  // One thrown error can reach several handlers (e.g. a chunk-load retry guard
  // and the route error boundary); record it once.
  const recordedErrors = new WeakSet<object>();

  return emitter.subscribe<UserErrorShownEvent>(
    DomainEventNames.UserErrorShown,
    (event) => {
      // Signed out: the write would 401 and feed the session circuit breaker.
      const profile = medplum.getProfile();
      if (!profile) return;

      if (typeof event.error === "object" && event.error !== null) {
        if (recordedErrors.has(event.error)) return;
        recordedErrors.add(event.error);
      }

      const auditEvent = buildUserErrorAuditEvent(
        event,
        profile,
        window.location,
      );
      const key = JSON.stringify([
        event.source,
        auditEvent.outcomeDesc,
        event.detail,
      ]);
      const now = Date.now();
      const last = lastRecordedAt.get(key);
      if (last !== undefined && now - last < USER_ERROR_DEDUPE_WINDOW_MS) {
        return;
      }
      lastRecordedAt.set(key, now);

      medplum.createResource(auditEvent).catch((err: unknown) => {
        console.warn(
          "[user-error-audit] AuditEvent write failed:",
          sanitizeTelemetryMessage(normalizeErrorString(err)),
        );
      });
    },
  );
};
